import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Loader2, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import { AboutPageSEO } from '../components/SEO';

export const AboutPage = () => {
    const [about, setAbout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedInstructor, setSelectedInstructor] = useState(null);

    useEffect(() => {
        const fetchAbout = async () => {
            try {
                const response = await api.get('/about');
                setAbout(response.data);
            } catch (err) {
                console.error('Failed to fetch about content:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAbout();
    }, []);

    if (loading) {
        return (
            <>
                <AboutPageSEO />
                <div className="min-h-[50vh] flex items-center justify-center" data-testid="about-loading">
                    <Loader2 className="h-8 w-8 animate-spin text-accent" />
                </div>
            </>
        );
    }

    if (!about) {
        return (
            <>
                <AboutPageSEO />
                <div className="min-h-[50vh] flex items-center justify-center" data-testid="about-error">
                    <p className="text-muted-foreground">Failed to load content</p>
                </div>
            </>
        );
    }

    return (
        <div data-testid="about-page">
            <AboutPageSEO />
            {/* Hero Section */}
            <section className="pt-12 md:pt-16 pb-6 md:pb-8 bg-secondary/30" data-testid="about-hero">
                <div className="container-main">
                    <div className="max-w-3xl">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            About Us
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            {about.title}
                        </h1>
                        <p className="text-xl text-muted-foreground mt-6 font-serif italic">
                            {about.tagline}
                        </p>
                    </div>
                </div>
            </section>

            {/* Mission Section */}
            <section className="py-10 md:py-14" data-testid="about-mission">
                <div className="container-main">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                        <div>
                            <h2 className="font-serif text-3xl font-medium text-primary mb-6">
                                Our Mission
                            </h2>
                            <p className="text-muted-foreground leading-relaxed text-lg">
                                {about.mission}
                            </p>
                        </div>
                        <div className="relative">
                            <img
                                src="https://customer-assets.emergentagent.com/job_oilpaint-edu/artifacts/rhxwlh8r_501302343_30270683282530219_3324887408145738278_n.jpg"
                                alt="Tony Pro teaching with model"
                                className="w-full rounded-sm shadow-lg"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Philosophy Section */}
            <section className="py-10 md:py-14 bg-secondary/30" data-testid="about-philosophy">
                <div className="container-main">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-8">
                            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                                Educational Foundation
                            </span>
                            <h2 className="font-serif text-3xl md:text-4xl font-medium text-primary mt-4">
                                Our Teaching Philosophy
                            </h2>
                        </div>
                        <div className="prose prose-lg max-w-none">
                            {about.philosophy.split('\n\n').map((paragraph, index) => (
                                <p key={index} className="text-muted-foreground leading-relaxed mb-6">
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Instructors Section */}
            <section className="py-10 md:py-14 bg-secondary/30" data-testid="about-instructors">
                <div className="container-main">
                    <div className="text-center mb-8">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Learn from the Best
                        </span>
                        <h2 className="font-serif text-3xl md:text-4xl font-medium text-primary mt-4">
                            Our Instructors
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {about.instructors.map((instructor, index) => {
                            const slug = instructor.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                            return (
                            <Card key={index} className="border-border/60 overflow-hidden hover:shadow-lg transition-shadow">
                                <Link to={`/instructor/${slug}`}>
                                    <div className="aspect-[4/3] bg-muted overflow-hidden">
                                        <img
                                            src={instructor.image}
                                            alt={instructor.name}
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                        />
                                    </div>
                                </Link>
                                <CardContent className="p-6">
                                    <Link to={`/instructor/${slug}`}>
                                        <h3 className="font-serif text-xl font-medium mb-3 hover:text-accent transition-colors">
                                            {instructor.name}
                                        </h3>
                                    </Link>
                                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3 mb-4">
                                        {instructor.bio.split('\n')[0]}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            onClick={() => setSelectedInstructor(instructor)}
                                            className="text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                                        >
                                            Read More
                                        </button>
                                        <span className="text-muted-foreground/50">•</span>
                                        <Link 
                                            to={`/instructor/${slug}`}
                                            className="inline-flex items-center text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                                        >
                                            View All Content
                                            <ArrowRight className="ml-1 h-4 w-4" />
                                        </Link>
                                    </div>
                                </CardContent>
                            </Card>
                            );
                        })}
                    </div>

                    {/* Instructor Bio Modal */}
                    <Dialog open={!!selectedInstructor} onOpenChange={(open) => !open && setSelectedInstructor(null)}>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            {selectedInstructor && (
                                <>
                                    <DialogHeader>
                                        <div className="flex flex-col sm:flex-row gap-6 items-start">
                                            <img
                                                src={selectedInstructor.image}
                                                alt={selectedInstructor.name}
                                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-lg flex-shrink-0"
                                            />
                                            <div>
                                                <DialogTitle className="font-serif text-2xl mb-2">
                                                    {selectedInstructor.name}
                                                </DialogTitle>
                                                {(selectedInstructor.course_id || selectedInstructor.video_id) && (
                                                    <Link 
                                                        to={selectedInstructor.course_id ? `/courses/${selectedInstructor.course_id}` : `/video/${selectedInstructor.video_id}`}
                                                        className="inline-flex items-center text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                                                        onClick={() => setSelectedInstructor(null)}
                                                    >
                                                        View Classes
                                                        <ArrowRight className="ml-1 h-4 w-4" />
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </DialogHeader>
                                    <div className="mt-4">
                                        {selectedInstructor.bio.split('\n\n').map((paragraph, idx) => (
                                            <p key={idx} className="text-muted-foreground leading-relaxed mb-4 last:mb-0">
                                                {paragraph}
                                            </p>
                                        ))}
                                    </div>
                                </>
                            )}
                        </DialogContent>
                    </Dialog>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-10 md:py-14 bg-primary text-primary-foreground" data-testid="about-cta">
                <div className="container-main text-center">
                    <h2 className="font-serif text-3xl md:text-4xl font-medium">
                        Ready to Begin Your Journey?
                    </h2>
                    <p className="text-primary-foreground/70 mt-4 max-w-2xl mx-auto">
                        Explore our courses and workshops to start learning from 
                        master artists today.
                    </p>
                    <div className="flex justify-center gap-4 mt-8">
                        <Button 
                            asChild 
                            size="lg" 
                            className="bg-accent text-accent-foreground hover:bg-accent/90"
                        >
                            <Link to="/courses">
                                View Courses
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                        <Button 
                            asChild 
                            variant="outline" 
                            size="lg"
                            className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                        >
                            <Link to="/workshops">
                                Browse Painting Videos
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
